import { Box, Container, Link, Stack, Typography, useTheme } from "@mui/material";
import { NavigationAndFooterLayout } from "../layouts";
import { NextPageWithLayout } from "@cm-websites/common";
import { NextSeo, HowToJsonLd } from "next-seo";
import Image from "next/image";
import Image01 from "../../public/images/krimidinner-grillparty/krimidinner-grillparty-im-garten.jpg";
import Image02 from "../../public/images/krimidinner-grillparty/gartenfeier.jpg";
import Image03 from "../../public/images/krimidinner-grillparty/krimidinner-draussen.jpg";
import Image04 from "../../public/images/krimidinner-grillparty/krimi-menue-grillen.jpg";
import Image05 from "../../public/images/krimidinner-grillparty/garten-krimidinner.jpg";
import NextLink from "next/link";
import React from "react";

export const Page: NextPageWithLayout = () => {
  const theme = useTheme();
  const styles = { h2: { pt: 5, pb: 2 } } as const;

  return (
    <React.Fragment>
      <NextSeo
        description="Mit diesen Schritten kannst du ein Krimidinner kinderleicht selber machen. Wir führen dich durch die einzelnen Schritte zum perfekten Krimidinner."
        title="Sommerliche Krimi-Grillparty: So gelingt dir dein Krimidinner im Garten"
      />

      <Container maxWidth="md" sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: 4 }}>
        <Typography variant="h1" sx={{ pb: 6, display: "block" }}>
          Sommerliche Krimi-Grillparty
          <Typography component="span" variant="h2" sx={{ display: "block", mt: 1 }}>
            So gelingt dein Krimidinner im Garten
          </Typography>
        </Typography>

        <Typography variant="body1" paragraph>
          Sommerzeit und der Duft von gegrilltem Fleisch und Gemüse liegt in der Luft. Die Grillsaison
          ist eröffnet. Egal, ob im eigenen Garten, auf der Balkonoase oder im naheliegenden Park –
          draußen im Grünen ist der perfekte Ort für eine spannende Krimidinner Party unter Freunden.
        </Typography>
      </Container>
      <Container maxWidth="lg" sx={{ pt: 0, pb: 0 }}>
        <Box>
          <Image
            alt="Krimidinner Rollenhefte auf einem gedeckten Tisch im Garten"
            placeholder="blur"
            src={Image01}
            title="Veranstalte eine Krimidinner Grillparty"
            style={{
              borderRadius: theme.shape.borderRadius,
              width: "100%",
              height: "auto",
              overflow: "hidden",
            }}
          />
        </Box>
      </Container>
      <Container maxWidth="md" sx={{ pt: 4, pb: 6 }}>
        <Typography variant="body1" paragraph>
          Culinario Mortale lässt sich hervorragend draußen im Freien planen und bietet eine einzigartige
          Mischung aus Spannung und Spaß. Überrasche deine Freunde zu diesem besonderen Anlass mit einem
          spannenden Kriminalfall, während der Grill duftende Köstlichkeiten vor sich brutzelt. Mit der
          richtigen Planung, Dekoration und einem gut durchdachten Menü wird deine Krimidinner-Grillparty
          sicher ein Erfolg.
        </Typography>

        <Typography variant="h2" sx={styles.h2}>
          Das passende Krimidinner aussuchen
        </Typography>

        <Typography variant="body1" paragraph>
          Eine Gartenparty macht so richtig was her, wenn sie unter einem bestimmten Motto veranstaltet
          wird, deswegen wähle ein witziges Krimidinner-Thema, das zu einer Outdoor-Umgebung passt. Wir
          empfehlen unseren beliebten Klassiker{" "}
          <NextLink href="/krimidinner/dauercamper-dosenbier-und-eine-leiche">
            „Dauercamper, Dosenbier und eine Leiche“
          </NextLink>
          , wo auf dem Campingplatz eine Leiche gefunden wird. Ebenso fesselnd ist der tödliche Fall im
          peruanischen Regenwald{" "}
          <NextLink href="/krimidinner/expedition-in-den-tod">„Expedition in den Tod”</NextLink>. Oder
          wie wäre es mit <NextLink href="/krimidinner/mord-ueber-bord">„Mord über Bord“</NextLink>? Ein
          Yachtausflug alter Schulfreunde nimmt eine tragische Wendung. Diese Themen bieten den perfekten
          Rahmen für ein Krimidinner im Freien.
        </Typography>

        <Typography variant="h2" sx={styles.h2}>
          Einladungen zur Gartenparty verschicken
        </Typography>

        <Typography variant="body1" paragraph>
          Jede gelungene Gartenfeier beginnt mit einer stilvollen Einladung. Personalisierte
          Einladungskarten sind eine großartige Möglichkeit, Deine Gäste einzuladen. Am
          unkompliziertesten und schnellsten geht es jedoch mit einer persönlichen Einladung per
          Nachricht. Sende die Einladungen frühzeitig aus, damit Deine Gäste genug Zeit haben, sich
          vorzubereiten. In der Einladung sollten das Thema, der Dresscode und grundlegende Informationen
          zum Ablauf des Abends enthalten sein. Digitale Einladungen können mit kreativen Designs und
          spannenden Texten die Vorfreude erhöhen. Hier haben wir auch ein{" "}
          <NextLink
            href="/images/krimidinner-grillparty/einladung-krimi-gartenparty.docx"
            download={true}
          >
            Beispiel
          </NextLink>
          , wie Deine Einladung aussehen könnte.
        </Typography>

        <Typography variant="body1" paragraph>
          Animiere deine Gäste dazu, sich dem Thema deines Krimidinners entsprechend zu verkleiden – die
          meisten werden viel Spaß dabei haben.
        </Typography>
      </Container>
      <Container maxWidth="lg" sx={{ pt: 0, pb: 0 }}>
        <Stack direction="row" sx={{ width: "100%" }} spacing={{ xs: 2, sm: 4 }}>
          <Box>
            <Image
              alt="Krimidinner draußen an einem festlich gedeckten Tisch im Garten"
              placeholder="blur"
              src={Image02}
              title="Krimidinner im Garten veranstalten"
              style={{
                borderRadius: theme.shape.borderRadius,
                width: "100%",
                height: "auto",
                overflow: "hidden",
              }}
            />
          </Box>

          <Box>
            <Image
              alt="Krimidinner Gartenparty"
              placeholder="blur"
              src={Image03}
              title="Krimidinner Gartenparty"
              style={{
                borderRadius: theme.shape.borderRadius,
                width: "100%",
                height: "auto",
                overflow: "hidden",
              }}
            />
          </Box>
        </Stack>
      </Container>
      <Container maxWidth="md" sx={{ pt: 4, pb: 6 }}>
        <Typography variant="h2" component="h3" sx={styles.h2}>
          Die richtige Atmosphäre im Garten schaffen
        </Typography>

        <Typography variant="body1" paragraph>
          Dekoriere den Garten passend zum Thema des Krimidinners. Nutze Lichterketten, Kerzen und
          thematische Dekorationen, um eine stimmungsvolle Atmosphäre zu schaffen. Ein geschmackvoll
          dekorierter Tisch, passende Sitzgelegenheiten und kleine Details wie Namenskärtchen für die
          Charaktere runden das Bild ab. Die Musik macht den Ton der Party: Zu jeder gelungenen Feier
          gehört einfach eine gute{" "}
          <NextLink href="https://open.spotify.com/user/xdswq2e8v8mt3l7i5mtthmzis">
            Musik-Playlist
          </NextLink>
          .
        </Typography>

        <Typography variant="h2" sx={styles.h2}>
          Das Menü
        </Typography>

        <Typography variant="body1" paragraph>
          Auf einer gelungenen Gartenparty darf leckeres Essen keinesfalls fehlen! Was bietet sich an
          lauen Sommerabenden besser an als Köstlichkeiten frisch vom Grill? Plane ein Menü, das sich
          perfekt für die Zubereitung auf dem Grill eignet und ideal zum Motto deines Krimidinners passt.
          Eine clevere Idee ist es, deine Gäste mit einzubeziehen: Bitte jeden, eine Leckerei
          mitzubringen – sei es ein erfrischender Salat, eine selbstgemachte Bowle oder ein köstlicher
          Kuchen. Auf diese Weise tragen alle zum Gelingen der Feier bei und können sich über Komplimente
          für das delikate Essen freuen.
        </Typography>
      </Container>
      <Container maxWidth="lg" sx={{ pt: 0, pb: 0 }}>
        <Stack direction="row" sx={{ width: "100%" }} spacing={{ xs: 2, sm: 4 }}>
          <Box>
            <Image
              alt="Gegrillte Köstlichkeiten mit dem passenden Fall"
              placeholder="blur"
              src={Image04}
              title="Dauercamper, Dosenbier und eine Leiche"
              style={{
                borderRadius: theme.shape.borderRadius,
                width: "100%",
                height: "auto",
                overflow: "hidden",
              }}
            />
          </Box>

          <Box>
            <Image
              alt="Grill-Gemüse und ein kaltes Getränk passen perfekt zu einem heißen Kriminalfall"
              placeholder="blur"
              src={Image05}
              style={{
                borderRadius: theme.shape.borderRadius,
                width: "100%",
                height: "auto",
                overflow: "hidden",
              }}
            />
          </Box>
        </Stack>
      </Container>
      <Container maxWidth="md" sx={{ pt: 4, pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}>
        <Typography variant="h2" component="h3" sx={styles.h2}>
          Pro Tipps
        </Typography>

        <ul>
          <Typography variant="body1" component="li">
            Kalte Füße vermeiden: Damit eure Party nicht von den sinkenden Temperaturen nach Einbruch der
            Nacht vorzeitig beendet wird, solltet ihr kuschelige Decken bereit halten.
          </Typography>

          <Typography variant="body1" component="li">
            Zum Schluss der wichtigste Tipp für ein rundum gelungenes Gartenfest: ein gut gelaunter und
            entspannter Gastgeber! Nutzt die langen, lauen Sommernächte und sammelt bleibende
            Erinnerungen, von denen ihr im Winter noch zehren könnt. Freut euch, dass ihr alle zusammen
            seid und mal wieder Zeit zum gemeinsamen Essen, Trinken und Lachen habt.
          </Typography>

          <Typography variant="body1" component="li">
            Falls du nach Inspiration, Rezepten oder Musik-Playlist sucht, schaut auf unserer Website
            vorbei, wir haben da einige Ideen zusammengefasst und ein Moodboard mit Inspirationen und
            Deko-Ideen erstellt. Außerdem findet ihr dort viele andere kreative Tipps, um einen
            unvergesslichen Grillabend mit Freunden und Familie zu haben.
          </Typography>
        </ul>

        <Typography variant="h2" component="h3" sx={styles.h2}>
          Erinnerung schaffen
        </Typography>

        <Typography variant="body1" paragraph>
          Vergesst nicht, Fotos zu machen, um die Erinnerungen an diesen besonderen Abend festzuhalten.
        </Typography>
      </Container>
    </React.Fragment>
  );
};

Page.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default Page;
